import { Close } from "@mui/icons-material";
import {
  Alert,
  AlertColor,
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { FormEvent, useCallback, useState } from "react";
import { useAddClinicianMutation } from "src/api/services/ePrescribeApi";
import { DetailedEmployee } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { desktopSx } from "src/utils/styles/modalStyles";

interface Props {
  open: boolean;
  onClose: () => void;
  user: DetailedEmployee;
}

const clinicianRoles = [
  "PrescribingClinician",
  "ReportingClinician",
  "EpcsCoordinator",
  "ClinicianAdmin",
  "PrescribingAgentClinician",
  "ProxyClinician",
];

const AddEPrescribeClinicianModal = ({ onClose, open, user }: Props) => {
  const [addClinician] = useAddClinicianMutation();
  const [loading, setLoading] = useState<boolean>(false);
  const [roles, setRoles] = useState<string[]>([]);
  const [alert, setAlert] = useState<{
    severity: AlertColor;
    message: string;
  } | null>(null);
  const errLog = useSentryLogger("error");

  const handleMultiSelect = useCallback((e: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = e;

    const newValue = typeof value === "string" ? value.split(",") : value;

    setRoles(newValue);
  }, []);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);

      if (
        user.first_name == null ||
        user.last_name == null ||
        user.dob == null
      ) {
        setAlert({
          severity: "error",
          message:
            "Oops, looks like we are missing some vital information. Please make sure this provider has all of the valid information before continuing.",
        });
        return;
      }

      try {
        await addClinician({
          employeeId: user.id,
          body: {
            FirstName: user.first_name,
            LastName: user.last_name,
            DateOfBirth: user.dob,
            NPINumber: user.provider ? user.provider.npi_key : null,
            ClinicianRoleType: roles,
          },
        }).unwrap();

        setAlert({
          severity: "success",
          message: "Successfully added clinician to the E-Prescribe service",
        });
      } catch (err) {
        setAlert({
          severity: "error",
          message:
            "Oops, looks like something went wrong. Please try again or notify the operations team.",
        });
        errLog("Unable to add clinician to E-Prescribe service", err);
      } finally {
        setLoading(false);
      }
    },
    [
      addClinician,
      errLog,
      roles,
      user.dob,
      user.first_name,
      user.id,
      user.last_name,
      user.provider,
    ],
  );

  const handleClose = useCallback(() => {
    setRoles([]);
    setAlert(null);
    onClose();
  }, [onClose]);

  return (
    <Modal open={open}>
      <Stack
        sx={desktopSx}
        gap={2}
        component="form"
        maxWidth={400}
        onSubmit={handleSubmit}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h5" fontWeight={700}>
            Add Clinician to E-Prescribe Service
          </Typography>
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <Close fontSize="medium" />
          </IconButton>
        </Box>
        <FormControl required>
          <InputLabel>Clinician roles</InputLabel>
          <Select
            required
            value={roles}
            multiple
            onChange={handleMultiSelect}
            label="Clinician roles"
          >
            {clinicianRoles.map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {alert ? (
          <Alert severity={alert.severity} variant="filled">
            {alert.message}
          </Alert>
        ) : null}
        <Typography>
          By pressing "Add" you will be adding this clinician to our E-Prescribe
          service
        </Typography>
        <LoadingButton loading={loading} type="submit">
          Add
        </LoadingButton>
      </Stack>
    </Modal>
  );
};

export default AddEPrescribeClinicianModal;
